import * as React from 'react';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import MenuContent from './MenuContent';
import OptionsMenu from './OptionsMenu';
import SitemarkIcon from './SitemarkIcon';

const EXPANDED_WIDTH = 256;
const COLLAPSED_WIDTH = 64;

const Drawer = styled(MuiDrawer)({
  flexShrink: 0,
  boxSizing: 'border-box',
  [`& .${drawerClasses.paper}`]: {
    backgroundColor: '#0f172a',
    backgroundImage: 'none',
    borderRight: 'none',
    boxShadow: '4px 0 20px rgba(0,0,0,0.25)',
    overflowX: 'hidden',
    transition: 'width 0.22s ease',
  },
});

export default function SideMenu() {
  const [collapsed, setCollapsed] = React.useState(false);

  const nombre = localStorage.getItem('nombre') || '';
  const usuario = localStorage.getItem('usuario') || '';
  const context =
    localStorage.getItem('nombre_sucursal') ||
    localStorage.getItem('nombre_compania') ||
    '';
  const initials = nombre
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        display: { xs: 'none', md: 'block' },
        transition: 'width 0.22s ease',
        [`& .${drawerClasses.paper}`]: { width },
      }}
    >
      {/* Logo + toggle */}
      <Box
        sx={{
          px: collapsed ? 1 : 2.5,
          pt: 'calc(var(--template-frame-height, 0px) + 16px)',
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: collapsed ? 'center' : 'stretch',
        }}
      >
        {collapsed ? (
          <IconButton
            onClick={() => setCollapsed(false)}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.7)', mb: 1, alignSelf: 'center' }}
          >
            <KeyboardDoubleArrowRightRoundedIcon fontSize="small" />
          </IconButton>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ filter: 'brightness(0) invert(1)', opacity: 0.9, flex: 1 }}>
                <SitemarkIcon height={32} width={500} />
              </Box>
              <IconButton
                onClick={() => setCollapsed(true)}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.5)', ml: 0.5 }}
              >
                <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <BusinessRoundedIcon sx={{ fontSize: 13, color: '#ffffff', flexShrink: 0 }} />
              <Typography
                variant="caption"
                noWrap
                sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 500, flex: 1, fontSize: '0.72rem' }}
              >
                {context}
              </Typography>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
            </Box>
          </>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />

      {/* Nav */}
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        <MenuContent collapsed={collapsed} />
      </Box>

      {/* User footer */}
      <Box sx={{ px: collapsed ? 1 : 2, pb: 2.5 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 1.5 }} />
        {collapsed ? (
          <Tooltip title={nombre} placement="right" arrow>
            <Avatar
              sx={{
                width: 34, height: 34,
                bgcolor: 'hsl(210,98%,42%)',
                fontSize: '0.78rem',
                fontWeight: 700,
                mx: 'auto',
                cursor: 'default',
              }}
            >
              {initials || nombre.charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
        ) : (
          <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 34, height: 34,
                bgcolor: 'hsl(210,98%,42%)',
                fontSize: '0.78rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials || nombre.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ mr: 'auto', minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)', lineHeight: 1.4, fontSize: '0.82rem' }}
              >
                {nombre}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}
              >
                {usuario}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.7)' } }}>
              <OptionsMenu />
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
